import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { ImageUpload } from '../../common/ImageUpload';
import type { Course } from '../../../types/course';
import { supabase } from '../../../lib/supabase';

interface CourseCreationFormProps {
  onSubmit: (courseData: Partial<Course>) => Promise<void>;
  initialData?: Course;
  onCancel: () => void;
}

export function CourseCreationForm({ onSubmit, initialData, onCancel }: CourseCreationFormProps) {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description?.trim() || '',
    category_id: initialData?.category_id || '',
    image_url: initialData?.image_url || '',
    duration: initialData?.duration || '',
    level: initialData?.level || 'Beginner',
    provider: initialData?.provider || '',
    affiliate_url: initialData?.affiliate_url || '',
    salary_range: initialData?.salary_range || '',
    has_certification: initialData?.has_certification || false,
    is_published: initialData?.is_published || false
  });
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('course_categories')
          .select('id, name')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
      } catch (error) {
        console.error('Error loading categories:', error);
        toast.error('Failed to load categories');
      }
    };

    loadCategories();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setValidationErrors([]);

    // Validate required fields
    const errors = [];
    if (!formData.title.trim()) errors.push('Course title is required');
    if (!formData.description.trim()) errors.push('Course description is required');
    if (!formData.image_url) errors.push('Course image is required');
    if (!formData.duration.trim()) errors.push('Course duration is required');
    if (!formData.provider.trim()) errors.push('Course provider is required');
    if (!formData.category_id) errors.push('Course category is required');
    if (!formData.salary_range.trim()) errors.push('Salary range is required');

    if (errors.length > 0) {
      setValidationErrors(errors);
      errors.forEach(error => toast.error(error));
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error saving course:', error);
      if (error instanceof Error) {
        setValidationErrors([error.message]);
        toast.error(error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <ul className="list-disc list-inside text-sm text-red-600">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-black mb-2">Course Image</label>
        <ImageUpload
          onUpload={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
          currentImageUrl={formData.image_url}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-black mb-2">Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="input w-full text-black"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-black mb-2">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={e => {
            const value = e.target.value;
            setFormData(prev => ({ 
              ...prev, 
              description: value
            }));
          }}
          onBlur={e => {
            // Trim on blur
            const value = e.target.value.trim();
            setFormData(prev => ({
              ...prev,
              description: value
            }));
          }}
          className="input w-full h-32 text-black"
          required
          placeholder="Enter course description..."
        />
        {formData.description && (
          <p className="mt-1 text-sm text-gray-500">
            {formData.description.length} characters
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-black mb-2">Category</label>
          <select
            value={formData.category_id}
            onChange={e => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
            className="input w-full"
            required
          >
            <option value="">Select a category</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-black mb-2">Duration</label>
          <input
            type="text"
            value={formData.duration}
            onChange={e => setFormData(prev => ({ ...prev, duration: e.target.value }))}
            className="input w-full text-black"
            placeholder="e.g. 6 weeks"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-black mb-2">Level</label>
          <select
            value={formData.level}
            onChange={e => setFormData(prev => ({ ...prev, level: e.target.value as Course['level'] }))}
            className="input w-full text-black"
          >
            <option>Beginner</option>
            <option>Intermediate</option>
            <option>Advanced</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-black mb-2">Provider</label>
          <input
            type="text"
            value={formData.provider}
            onChange={e => setFormData(prev => ({ ...prev, provider: e.target.value }))}
            className="input w-full text-black"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-black mb-2">Affiliate Link</label>
        <input
          type="url"
          value={formData.affiliate_url ?? ''}
          onChange={e => {
            const value = e.target.value;
            setFormData(prev => ({
              ...prev,
              affiliate_url: value
            }));
          }}
          onBlur={e => {
            const value = e.target.value.trim();
            setFormData(prev => ({
              ...prev,
              affiliate_url: value
            }));
          }}
          className="input w-full text-black"
          placeholder="https://example.com/affiliate-link"
          pattern="^$|https?://.*"
        />
        <p className="mt-1 text-sm text-gray-500">
          Optional: Add your affiliate link for this course (must start with http:// or https://)
          )
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-black mb-2">
            Expected Salary Range
          </label>
          <input
            type="text"
            value={formData.salary_range}
            onChange={e => setFormData(prev => ({ ...prev, salary_range: e.target.value }))}
            className="input w-full text-black"
            placeholder="e.g. $70k - $110k"
            required
          />
          <p className="mt-1 text-sm text-gray-500">
            Enter the expected salary range for this role
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-black mb-2">
            Certification
          </label>
          <div className="flex items-center space-x-2 mt-2">
            <input
              type="checkbox"
              id="has_certification"
              checked={formData.has_certification}
              onChange={e => setFormData(prev => ({ ...prev, has_certification: e.target.checked }))}
              className="rounded border-gray-700 text-accent-green focus:ring-accent-green bg-background-dark"
            />
            <label htmlFor="has_certification" className="text-sm text-black">
              Certification program available
            </label>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Check if this course offers a professional certification
          </p>
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="is_published"
          checked={formData.is_published}
          onChange={e => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
          className="rounded border-gray-700 text-accent-green focus:ring-accent-green bg-background-dark"
        />
        <label htmlFor="is_published" className="ml-2 text-sm text-black">
          Publish course
        </label>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="mr-4 px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary"
        >
          {isSubmitting ? 'Saving...' : initialData ? 'Update Course' : 'Create Course'}
        </button>
      </div>
    </form>
  );
}